/**
 * Pure functions for extracting tool call data from assistant-ui messages.
 *
 * These functions operate on message arrays without any React dependencies,
 * making them testable and reusable across different contexts.
 */

import { isMessage, isToolCallPart, type ToolCallPart } from './output-types.ts';

/**
 * Extract all tool call parts from a list of messages.
 * Filters to assistant messages only and extracts tool-call content parts.
 *
 * @param messages - Array of assistant-ui messages (readonly for immutability)
 * @returns Array of tool call parts from assistant messages
 */
export function extractToolCallParts(messages: readonly unknown[]): ToolCallPart[] {
  return messages
    .filter(isMessage)
    .filter((msg) => msg.role === 'assistant')
    .flatMap((msg) => msg.content)
    .filter(isToolCallPart);
}

/**
 * Find a tool call part by its outputDataset argument.
 * Used when a downstream tool references data by output key.
 *
 * @param parts - Array of tool call parts to search
 * @param outputDataset - The output key to find
 * @returns The matching tool call part, or undefined if not found
 */
export function findToolCallByOutputKey(
  parts: readonly ToolCallPart[],
  outputDataset: string,
): ToolCallPart | undefined {
  return parts.toReversed().find((part) => part.args.outputDataset === outputDataset);
}

/**
 * Find a tool call part by its toolCallId.
 * Used for looking up specific tool invocations.
 *
 * @param parts - Array of tool call parts to search
 * @param toolCallId - The tool call ID to find
 * @returns The matching tool call part, or undefined if not found
 */
export function findToolCallById(parts: readonly ToolCallPart[], toolCallId: string): ToolCallPart | undefined {
  return parts.toReversed().find((part) => part.toolCallId === toolCallId);
}

/**
 * Extract unique output keys from tool call parts.
 * Filters out parts without an outputDataset and deduplicates.
 *
 * @param parts - Array of tool call parts to extract keys from
 * @returns Array of unique output key strings
 */
export function extractOutputKeys(parts: readonly ToolCallPart[]): string[] {
  return [
    ...new Set(parts.map((part) => part.args.outputDataset).filter((key): key is string => typeof key === 'string')),
  ];
}
